import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Tracks which guided walkthroughs the user has completed and whether tours
 * are enabled overall. Mirrored to localStorage so anonymous visitors don't
 * see tours twice, and so logged-in users don't see a flash before the
 * server roundtrip lands.
 */
type TourName = "welcome" | "hub";

type LocalState = {
  welcomeAt: string | null;
  hubAt: string | null;
  enabled: boolean;
};

const LS_KEY = "hl_tour_state";

function readLocal(): LocalState {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { welcomeAt: null, hubAt: null, enabled: true, ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return { welcomeAt: null, hubAt: null, enabled: true };
}

function writeLocal(patch: Partial<LocalState>) {
  try {
    const next = { ...readLocal(), ...patch };
    localStorage.setItem(LS_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

export function useTourProgress() {
  const { user } = useAuth();
  const [state, setState] = useState<LocalState>(() => readLocal());
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const local = readLocal();
      if (!user) {
        if (!cancelled) {
          setState(local);
          setReady(true);
        }
        return;
      }
      try {
        const { data } = await supabase
          .from("tour_progress")
          .select("welcome_completed_at, hub_completed_at, tours_enabled")
          .eq("user_id", user.id)
          .maybeSingle();
        const merged: LocalState = data
          ? {
              welcomeAt: (data as any).welcome_completed_at ?? local.welcomeAt,
              hubAt: (data as any).hub_completed_at ?? local.hubAt,
              enabled: (data as any).tours_enabled ?? local.enabled,
            }
          : local;
        writeLocal(merged);
        if (!cancelled) {
          setState(merged);
          setReady(true);
        }
      } catch {
        if (!cancelled) {
          setState(local);
          setReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const persist = useCallback(
    async (patch: Partial<LocalState>) => {
      const next = { ...state, ...patch };
      setState(next);
      writeLocal(patch);
      if (!user) return;
      try {
        await supabase.from("tour_progress").upsert(
          {
            user_id: user.id,
            welcome_completed_at: next.welcomeAt,
            hub_completed_at: next.hubAt,
            tours_enabled: next.enabled,
          },
          { onConflict: "user_id" },
        );
      } catch {
        /* localStorage already updated; live with eventual consistency */
      }
    },
    [state, user],
  );

  const markCompleted = useCallback(
    (tour: TourName) =>
      persist(tour === "welcome" ? { welcomeAt: new Date().toISOString() } : { hubAt: new Date().toISOString() }),
    [persist],
  );

  const resetTour = useCallback(
    (tour: TourName) =>
      persist(tour === "welcome" ? { welcomeAt: null } : { hubAt: null }),
    [persist],
  );

  const setEnabled = useCallback((enabled: boolean) => persist({ enabled }), [persist]);

  return {
    ready,
    enabled: state.enabled,
    welcomeCompleted: !!state.welcomeAt,
    hubCompleted: !!state.hubAt,
    markCompleted,
    resetTour,
    setEnabled,
  };
}